import React from 'react';
import { AutoComplete } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
	CargoUnitLabels,
	CargoUnitTypes,
	QuantityOptions,
} from '@shared/utils/constants';
import type { CargoDetails } from '@api/features/cargos/getCargoDetails';
import type { CargoTypeProps } from '@api/models/cargo-type';
import type { Port } from '@api/utils/ports';
import Select from '@client/components/Select';
import NumericInput from '@client/components/NumericInput';
import type { OnUpdateCargo } from '@client/screens/estimates/details/context/hooks/useCargoHandlers';
import sharedStyles from '@client/screens/estimates/details/styles/shared.module.css';

type PortOption = {
	label: string;
	value: number;
};

const getPortNames = (
	ports: Array<Port | number> | null | undefined,
	portOptions: PortOption[],
) => (ports ?? [])
	.map((port) => (
		typeof port === 'object' ?
			port.name :
			portOptions.find((option) => option.value === port)?.label
	))
	.filter((name) => name != null)
	.join(', ');

const getIntakeCargoColumns = ({
	onUpdateCargo,
	cargoTypes,
	portOptions,
	disabled,
}: {
	onUpdateCargo: OnUpdateCargo;
	cargoTypes: CargoTypeProps[];
	portOptions: PortOption[];
	disabled: boolean;
}): ColumnsType<CargoDetails> => [
	{
		title: 'ID',
		dataIndex: 'id',
		key: 'id',
		width: 15,
		onCell: () => ({ className: sharedStyles.calculated }),
	},
	{
		title: 'Commodity',
		dataIndex: 'type',
		key: 'type',
		width: 160,
		render: (value: string, row) => (
			<AutoComplete
				disabled={disabled}
				value={value}
				variant="borderless"
				placeholder="Commodity"
				size="small"
				onChange={(newValue: string) => {
					onUpdateCargo(row.id, {
						type: newValue,
					});
				}}
				onSelect={(newValue: string) => {
					const cargoType = cargoTypes.find((ct) => ct.name === newValue);

					onUpdateCargo(row.id, {
						type: newValue,
						...(cargoType?.stowageFactorCBFT != null && {
							stowageFactor: cargoType.stowageFactorCBFT,
						}),
					});
				}}
				filterOption={(inputValue: string, option?: { label: string }) => (
					option?.label?.toUpperCase().includes(inputValue.toUpperCase()) ?? false
				)}
				options={cargoTypes.map((ct) => ({
					label: ct.name,
					value: ct.name,
				}))}
			/>
		),
	},
	{
		title: 'Stowage factor',
		dataIndex: 'stowageFactor',
		key: 'stowageFactor',
		width: 150,
		render: (value: number | null, row) => (
			<NumericInput
				disabled={disabled}
				variant="borderless"
				value={value}
				addonAfter="cbft/ton"
				placeholder="Stowage factor"
				onChange={(newValue) => {
					onUpdateCargo(row.id, {
						stowageFactor: newValue,
					});
				}}
			/>
		),
	},
	{
		title: 'Loadports',
		dataIndex: 'loadingPorts',
		key: 'loadingPorts',
		onCell: () => ({ className: sharedStyles.calculated }),
		render: (ports: Array<Port | number>) => getPortNames(ports, portOptions) || '-',
	},
	{
		title: 'Disports',
		dataIndex: 'dischargePorts',
		key: 'dischargePorts',
		onCell: () => ({ className: sharedStyles.calculated }),
		render: (ports: Array<Port | number>) => getPortNames(ports, portOptions) || '-',
	},
	{
		title: 'CP quantity',
		dataIndex: 'quantity',
		key: 'quantity',
		width: 130,
		render: (value: number, row) => (
			<NumericInput
				isRequiredHighlight={!value}
				disabled={disabled}
				variant="borderless"
				value={value}
				placeholder="Quantity"
				onChange={(newValue) => {
					onUpdateCargo(row.id, {
						quantity: Number(newValue),
					});
				}}
			/>
		),
	},
	{
		title: 'Units',
		dataIndex: 'unit',
		key: 'unit',
		width: 110,
		render: (value: CargoDetails['unit'], row) => (
			<Select
				disabled={disabled}
				variant="borderless"
				value={value}
				size="small"
				placeholder="Unit"
				fullWidth
				options={Object.keys(CargoUnitTypes).map((unit) => ({
					label: CargoUnitLabels[unit as keyof typeof CargoUnitLabels],
					value: unit,
				}))}
				onChange={(newValue) => {
					if (newValue == null) {
						return;
					}

					onUpdateCargo(row.id, {
						unit: newValue as CargoDetails['unit'],
					});
				}}
				showSearch
			/>
		),
	},
	{
		title: 'Tolerance',
		dataIndex: 'quantityTolerance',
		key: 'quantityTolerance',
		width: 110,
		render: (value: number | null, row) => (
			<NumericInput
				disabled={disabled}
				variant="borderless"
				value={value}
				addonAfter="%"
				placeholder="Tolerance"
				onChange={(newValue) => {
					onUpdateCargo(row.id, {
						quantityTolerance: newValue,
					});
				}}
			/>
		),
	},
	{
		title: 'Option',
		dataIndex: 'quantityOption',
		key: 'quantityOption',
		width: 130,
		render: (value: QuantityOptions | null, row) => (
			<Select<QuantityOptions>
				disabled={disabled}
				variant="borderless"
				value={value}
				size="small"
				placeholder="Option"
				fullWidth
				options={[
					{
						label: 'None',
						value: null,
					},
					...Object.values(QuantityOptions).map((option) => ({
						label: option,
						value: option,
					})),
				]}
				onChange={(newValue) => {
					onUpdateCargo(row.id, {
						quantityOption: newValue,
					});
				}}
			/>
		),
	},
];

export default getIntakeCargoColumns;
